import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export type WorkflowRuntimeConfig = {
  /** Base URL for los_backend (e.g. https://host:port/api). */
  apiUrl: string;
};

export const WORKFLOW_RUNTIME_CONFIG = new InjectionToken<WorkflowRuntimeConfig>(
  'WORKFLOW_RUNTIME_CONFIG',
);

export type ProxyRequest = {
  method?: string;
  endpoint: string;
  query?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  body?: unknown;
  /** When false, failed proxy calls are not retried as direct browser HTTP calls. */
  allowDirectFallback?: boolean;
};

export type InvokeRequest = {
  category: string;
  operation: string;
  endpoint?: string;
  method?: string;
  headers?: Record<string, string>;
  body?: unknown;
  configuration?: Record<string, unknown>;
};

/**
 * Routes dataset/record/integration calls through los_backend so credentials
 * never live in the browser. Relative endpoints are resolved against apiUrl.
 */
@Injectable({ providedIn: 'root' })
export class BackendProxyService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(WORKFLOW_RUNTIME_CONFIG, { optional: true });

  private get apiUrl(): string {
    return (this.config?.apiUrl ?? '').replace(/\/$/, '');
  }

  /**
   * Route pack dataSource / optionsConfig calls through los_backend proxy.
   * Absolute and relative endpoints both go through Nest so query params are
   * preserved; relative paths fall back to a direct authenticated call.
   */
  async proxy<T = unknown>(req: ProxyRequest): Promise<T> {
    const method = (req.method ?? 'GET').toUpperCase();
    const endpoint = req.endpoint;
    const proxyUrl = `${this.apiUrl}/workflow-runtime/proxy`;

    try {
      const res = await firstValueFrom(
        this.http.post<{ success?: boolean; data?: T } | T>(proxyUrl, {
          method,
          endpoint,
          query: req.query,
          headers: req.headers,
          body: req.body,
        }),
      );
      if (res && typeof res === 'object' && 'data' in (res as object)) {
        return (res as { data: T }).data;
      }
      return res as T;
    } catch (error) {
      // Fall back to direct authenticated call for relative LOS paths only.
      if (req.allowDirectFallback !== false && !isAbsoluteUrl(endpoint)) {
        return this.directCall<T>(method, endpoint, req);
      }
      throw error;
    }
  }

  async invoke<T = unknown>(req: InvokeRequest): Promise<T> {
    const url = `${this.apiUrl}/workflow-runtime/integrations/invoke`;
    try {
      const res = await firstValueFrom(
        this.http.post<{ success?: boolean; data?: T } | T>(url, req),
      );
      if (res && typeof res === 'object' && 'data' in (res as object)) {
        return (res as { data: T }).data;
      }
      return res as T;
    } catch (error) {
      // Soft-fail for categories that may not have adapters yet in early envs.
      if (req.endpoint && !isAbsoluteUrl(req.endpoint)) {
        return this.directCall<T>(req.method ?? 'POST', req.endpoint, {
          endpoint: req.endpoint,
          method: req.method,
          headers: req.headers,
          body: req.body,
        });
      }
      throw error;
    }
  }

  /** Strip credential-bearing dataSource before handing configs to UI panels. */
  sanitizeDataSourceConfig(config: Record<string, unknown>): Record<string, unknown> {
    const clone = structuredClone(config) as Record<string, unknown>;
    const ds = clone['dataSource'];
    if (ds && typeof ds === 'object') {
      const safe = { ...(ds as Record<string, unknown>) };
      delete safe['credentialId'];
      delete safe['auth'];
      delete safe['headers'];
      // Keep endpoint/method/query/body/responseMapping for relative proxy calls.
      clone['dataSource'] = safe;
    }
    return clone;
  }

  private async directCall<T>(
    method: string,
    endpoint: string,
    req: Pick<ProxyRequest, 'query' | 'headers' | 'body' | 'endpoint' | 'method'>,
  ): Promise<T> {
    const path = normalizeProxyPath(endpoint, this.apiUrl);
    const url = `${this.apiUrl}${path}`;
    let params = new HttpParams();
    if (req.query) {
      for (const [k, v] of Object.entries(req.query)) {
        if (v !== undefined && v !== null && v !== '') params = params.set(k, String(v));
      }
    }
    const headers = new HttpHeaders(req.headers ?? {});
    const m = method.toUpperCase();
    if (m === 'GET') {
      return firstValueFrom(this.http.get<T>(url, { params, headers }));
    }
    if (m === 'DELETE') {
      return firstValueFrom(this.http.delete<T>(url, { params, headers }));
    }
    if (m === 'PUT') {
      return firstValueFrom(this.http.put<T>(url, req.body ?? {}, { params, headers }));
    }
    if (m === 'PATCH') {
      return firstValueFrom(this.http.patch<T>(url, req.body ?? {}, { params, headers }));
    }
    return firstValueFrom(this.http.post<T>(url, req.body ?? {}, { params, headers }));
  }
}

function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/** Avoid `/api/v1` + `/api/v1/...` when apiUrl already includes the version prefix. */
function normalizeProxyPath(endpoint: string, apiUrl: string): string {
  let path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const base = apiUrl.replace(/\/$/, '');
  const versionSuffix = base.match(/\/api\/v\d+$/i)?.[0];
  if (versionSuffix && path.toLowerCase().startsWith(`${versionSuffix.toLowerCase()}/`)) {
    path = path.slice(versionSuffix.length) || '/';
  }
  return path;
}
